class WishlistSummaryDrawer extends HTMLElement {
  connectedCallback() {
    this.sheet = this.querySelector('sheet-dialog');
    this.items = this.querySelector('[data-summary-items]');
    this.empty = this.querySelector('[data-summary-empty]');
    this.removeIconHtml = this.querySelector('[data-summary-remove-icon]')?.innerHTML || '×';
    this.loadingTemplate = this.querySelector('[data-summary-loading-template]');
    this.itemTemplate = this.querySelector('[data-summary-item-template]');
    this.sheet.addEventListener('sheet:close', () => {
      const cartDrawer = this.cartDrawerToRestore;
      this.cartDrawerToRestore = null;
      if (cartDrawer && !cartDrawer.open) cartDrawer.show();
    });
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-wishlist-summary-trigger]');
      if (!trigger) return;
      event.preventDefault();
      event.stopPropagation();
      this.open(trigger);
    }, true);
    window.addEventListener('wishlist:updated', (event) => {
      // Skip when we were the dispatcher — removeFromWishlist()/removeFromWishlistSilently()
      // already update this drawer's own DOM directly, so a full reload here is redundant.
      if (event.detail?.source === this) return;
      if (this.isOpen) this.load();
    });
  }

  get isOpen() {
    return this.sheet.hasAttribute('open');
  }

  // cartDrawerToRestore: the cart drawer that was open just before the wishlist flow started,
  // shown again when this closes. Header triggers pass nothing, so the cart stays closed.
  open(trigger, { cartDrawerToRestore = null } = {}) {
    this.cartDrawerToRestore = cartDrawerToRestore;
    this.sheet.open(trigger);
    this.load();
  }

  load() {
    if (!window._swat) {
      this.items.replaceChildren(this.loadingTemplate.content.cloneNode(true));
      window.SwymCallbacks = window.SwymCallbacks || [];
      window.SwymCallbacks.push(() => this.load());
      return;
    }
    window._swat.fetch((products) => this.render(products || []));
  }

  render(products) {
    this.empty.classList.toggle('hidden', products.length > 0);
    this.items.replaceChildren(...products.map((item) => this.buildItem(item)));
  }

  buildItem(item) {
    const article = this.itemTemplate.content.firstElementChild.cloneNode(true);
    article.dataset.summaryItem = item.epi;
    const variantUrl = `${item.du}${item.du?.includes('?') ? '&' : '?'}variant=${item.epi}`;

    const link = article.querySelector('[data-summary-item-link]');
    link.href = variantUrl;
    const image = article.querySelector('[data-summary-item-image]');
    image.src = item.iu || '';
    image.alt = item.dt || '';

    const titleLink = article.querySelector('[data-summary-item-title-link]');
    titleLink.href = variantUrl;
    titleLink.textContent = item.dt || '';

    const sizeEl = article.querySelector('[data-summary-item-size]');
    if (item.vi) {
      sizeEl.textContent = `${this.dataset.sizeLabel || 'Size'}: ${item.vi.split('/')[0]}`;
    } else {
      sizeEl.remove();
    }

    article.querySelector('[data-summary-item-price]').textContent = this.formatPrice(item.pr);

    const addButton = article.querySelector('[data-summary-add]');
    addButton.dataset.variantId = item.epi;
    addButton.addEventListener('click', () => this.addToCart(addButton, item));

    const removeButton = article.querySelector('[data-summary-remove]');
    const removeLabel = `${this.dataset.removeLabel || 'Remove'} ${item.dt || ''}`;
    removeButton.setAttribute('aria-label', removeLabel);
    removeButton.title = removeLabel;
    removeButton.dataset.productId = item.empi;
    removeButton.dataset.variantId = item.epi;
    removeButton.dataset.productUrl = item.du || '';
    removeButton.innerHTML = this.removeIconHtml;
    removeButton.addEventListener('click', () => this.removeFromWishlist(removeButton));

    return article;
  }

  formatPrice(dollars) {
    return window.Shopify.formatMoney(Number(dollars || 0) * 100, window.Shopify.money_format);
  }

  removeFromWishlist(button) {
    button.disabled = true;
    const item = button.closest('[data-summary-item]');
    const payload = {
      empi: Number(button.dataset.productId),
      epi: Number(button.dataset.variantId),
      du: button.dataset.productUrl,
    };
    // Optimistic: empty the icon/collapse the row immediately for INP rather than waiting on
    // the network round trip. Reconcile from the server (re-filling the item) on failure.
    window._swat.api.removeFromWishList(payload, () => {
      window.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { source: this } }));
    }, () => {
      console.error('Failed to remove item from wishlist', payload);
      this.load();
    });
    item.classList.remove('py-4');
    window.collapse(item).then(() => {
      item.remove();
      const remaining = this.items.children.length;
      this.empty.classList.toggle('hidden', remaining > 0);
      this.updateHeaderWishlistCount(remaining);
    });
  }

  updateHeaderWishlistCount(count) {
    document.querySelectorAll('.wishlist-count').forEach((element) => {
      element.dataset.count = String(count);
      const ariaLiveElement = element.querySelector('[aria-live]');
      if (ariaLiveElement) ariaLiveElement.textContent = String(count);
    });
  }

  async addToCart(button, item) {
    button.disabled = true;
    const originalLabel = button.textContent;
    button.textContent = this.dataset.addingLabel || 'Adding…';
    const error = button.parentElement.querySelector('[data-summary-error]');
    error.textContent = '';
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: Number(button.dataset.variantId), quantity: 1, ...(window.cartAddSections || {}) }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || window.cartStrings?.addToCartError || 'Unable to add item.');
      const cartContentHtml = window.extractCartContentHtml ? window.extractCartContentHtml(data) : null;
      window.dispatchEvent(new CustomEvent('cart-updated', { detail: { cartContentHtml } }));
      window.dispatchEvent(new CustomEvent('cart-drawer:open'));
      this.removeFromWishlistSilently(item);
      this.close();
    } catch (exception) {
      error.textContent = exception.message;
    } finally {
      button.disabled = false;
      button.textContent = originalLabel;
    }
  }

  // Cart add succeeded, so the item shouldn't linger in the wishlist — the
  // drawer is closing regardless, so skip removeFromWishlist's row
  // collapse/removal and just sync Swym + the rest of the page's wishlist state.
  removeFromWishlistSilently(item) {
    const payload = { empi: Number(item.empi), epi: Number(item.epi), du: item.du };
    window._swat.api.removeFromWishList(payload, () => {
      window.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { source: this } }));
      window._swat.fetch((products) => this.updateHeaderWishlistCount((products || []).length));
    });
  }

  close() {
    this.sheet.close();
  }
}
if (!customElements.get('wishlist-summary-drawer')) customElements.define('wishlist-summary-drawer', WishlistSummaryDrawer);
